'use client'

import {
  AtSign, CalendarDays, Copy, Gift, Heart, Image as ImageIcon, Link as LinkIcon, Mail, MapPin, Phone, Play, ShoppingBag, Ticket,
} from 'lucide-react'
import { SiMessenger, SiMessengerHex, SiTelegram, SiTelegramHex } from '@icons-pack/react-simple-icons'

import type { BlockGroup, BlockType } from '@/lib/dashboard'
import { PetSprite, type PetLookInput } from '@/components/pet/PetSprite'

const MINI_PET: PetLookInput = { species: 'mochi', color: 'peach', accessories: [], scene: null }

/**
 * Each catalog group's mood, as a pair of colours.
 *
 * The catalog used to be one grey grid where a poll and a payment looked like
 * the same thing with a different word on it. A colour per group lets the eye
 * sort before it reads: blue for the everyday blocks, violet for media, the
 * brand's own orange for the playful ones, green where money changes hands,
 * amber for support, lime for layout and tools. Never grey: a locked tile is
 * drawn greyscale, and a grey group would look locked. Layout stays lime end
 * to end: a lime into cyan pair met in a mid green right where the drawing
 * sits, and read as commerce's twin.
 */
export const GROUP_TONE: Record<BlockGroup, { from: string, to: string }> = {
  basic: { from: '#38bdf8', to: '#6366f1' },
  media: { from: '#a855f7', to: '#ec4899' },
  interactive: { from: '#f97316', to: '#ef4444' },
  commerce: { from: '#10b981', to: '#14b8a6' },
  support: { from: '#f59e0b', to: '#f43f5e' },
  layout: { from: '#84cc16', to: '#4d7c0f' },
}

/** A five-by-five stand-in for a QR code: three corner finders and some noise. */
const QR_MINI = [1, 1, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 1, 0, 0, 1, 0, 0, 1, 1, 1, 1, 0, 1, 0]

/** Three contact buttons, the first one leading: two services and a call. */
const CHAT_MINI = [
  { Icon: SiMessenger, color: SiMessengerHex },
  { Icon: SiTelegram, color: SiTelegramHex },
  { Icon: Phone, color: '#16A34A' },
]

/**
 * A block, drawn small: what it becomes on the page, rather than an icon and a
 * sentence about it.
 *
 * Shown in the catalog tile's stage. Each one moves a little when its tile is
 * hovered or focused (`group-hover`, `group-focus-visible`), and only then:
 * the motion answers the pointer, it is not decoration running on its own.
 * Text inside is numbers and marks only, so nothing here needs translating.
 */
export function BlockMini({ type, group }: { type: BlockType, group: BlockGroup }) {
  const { from, to } = GROUP_TONE[group]
  const gradient = { background: `linear-gradient(135deg, ${from}, ${to})` }
  const live = 'transition-transform duration-500 ease-out motion-reduce:transition-none'
  const hover = (classes: string) => `${live} ${classes}`

  switch(type) {
  case 'link':
    return (
      <span className={hover('flex h-7 items-center gap-1.5 rounded-full bg-white px-3 text-[10px] font-bold text-neutral-900 shadow-lg group-hover:-translate-y-0.5 group-focus-visible:-translate-y-0.5')}>
        <LinkIcon className="size-3" style={{ color: to }} aria-hidden="true" />
        fuse.mn/you
      </span>
    )

  case 'social':
    return (
      <span className="relative flex items-center">
        { [from, to, '#ffffff'].map((color, index) => (
          <span
            key={color}
            className={hover(`-ml-2 flex size-9 items-center justify-center rounded-full border-2 border-black/20 shadow-md first:ml-0 ${index === 1 ? 'z-10 group-hover:-translate-y-1 group-focus-visible:-translate-y-1' : ''}`)}
            style={{ background: color }}
          >
            { index === 1 && <AtSign className="size-4 text-white" aria-hidden="true" /> }
          </span>
        )) }
      </span>
    )

  case 'text':
    return (
      <span className="flex items-end gap-2.5">
        <span className="font-gilroy text-[34px] font-bold leading-none text-white">Aa</span>
        <span className="flex flex-col gap-1.5 pb-1">
          <span className={hover('h-1.5 w-14 origin-left rounded-full bg-white/80 group-hover:scale-x-110')} />
          <span className="h-1.5 w-10 rounded-full bg-white/50" />
          <span className={hover('h-1.5 w-12 origin-left rounded-full bg-white/30 group-hover:scale-x-75')} />
        </span>
      </span>
    )

  case 'cv':
    return (
      <span className={hover('flex w-32 items-center gap-2 rounded-xl bg-white/95 p-2 shadow-lg group-hover:-rotate-2 group-focus-visible:-rotate-2')}>
        <span className="size-7 shrink-0 rounded-full" style={gradient} />
        <span className="flex flex-1 flex-col gap-1">
          <span className="h-1.5 w-full rounded-full bg-neutral-800" />
          <span className="h-1.5 w-3/4 rounded-full bg-neutral-300" />
          <span className="mt-0.5 h-2 w-8 rounded-full" style={{ background: `${from}55` }} />
        </span>
      </span>
    )

  case 'image':
    return (
      <span className="relative h-14 w-20">
        <span className={hover('absolute inset-0 rotate-[-8deg] rounded-lg shadow-md group-hover:rotate-[-14deg]')} style={{ background: to }} />
        <span className={hover('absolute inset-0 flex rotate-[5deg] items-center justify-center rounded-lg bg-white shadow-lg group-hover:rotate-[9deg]')}>
          <ImageIcon className="size-6" style={{ color: from }} aria-hidden="true" />
        </span>
      </span>
    )

  case 'video':
    return (
      <span className="flex aspect-video w-24 items-center justify-center rounded-lg bg-neutral-950/80 shadow-lg ring-1 ring-white/15">
        <span className={hover('flex size-8 items-center justify-center rounded-full bg-white group-hover:scale-110 group-focus-visible:scale-110')}>
          <Play className="ml-0.5 size-3.5 fill-neutral-900 text-neutral-900" aria-hidden="true" />
        </span>
      </span>
    )

  case 'audio':
    return (
      <span className="flex h-10 items-center gap-[3px]">
        { [40, 70, 55, 90, 65, 100, 45, 80, 60, 35, 75, 50].map((height, index) => (
          <span
            key={index}
            className={hover(`w-[3px] rounded-full bg-white ${index % 2 ? 'group-hover:scale-y-50' : 'group-hover:scale-y-125'}`)}
            style={{ height: `${height}%`, transitionDelay: `${index * 25}ms` }}
          />
        )) }
      </span>
    )

  case 'product':
    return (
      <span className={hover('flex flex-col items-center gap-1.5 group-hover:-translate-y-1 group-focus-visible:-translate-y-1')}>
        <span className="flex size-11 items-center justify-center rounded-xl bg-white shadow-lg">
          <ShoppingBag className="size-5" style={{ color: to }} aria-hidden="true" />
        </span>
        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums text-white shadow" style={gradient}>45,000₮</span>
      </span>
    )

  case 'booking':
    return (
      <span className="flex items-center gap-2">
        <span className="grid grid-cols-4 gap-1">
          { Array.from({ length: 8 }, (_, index) => (
            <span
              key={index}
              className={index === 5
                ? hover('size-3.5 rounded-[4px] shadow group-hover:scale-125 group-focus-visible:scale-125')
                : 'size-3.5 rounded-[4px] bg-white/25'}
              style={index === 5 ? { background: '#fff' } : undefined}
            />
          )) }
        </span>
        <span className="flex items-center gap-1 rounded-full bg-white px-2 py-1 text-[10px] font-bold tabular-nums text-neutral-900 shadow">
          <CalendarDays className="size-3" style={{ color: to }} aria-hidden="true" />
          10:00
        </span>
      </span>
    )

  case 'ticket':
    return (
      <span className={hover('relative flex h-12 w-28 items-center rounded-lg bg-white shadow-lg group-hover:rotate-[-4deg] group-focus-visible:rotate-[-4deg]')}>
        {/* The stub's notches and its tear line. */}
        <span className="absolute -left-1.5 top-1/2 size-3 -translate-y-1/2 rounded-full bg-black/40" />
        <span className="absolute -right-1.5 top-1/2 size-3 -translate-y-1/2 rounded-full bg-black/40" />
        <span className="flex flex-1 items-center justify-center">
          <Ticket className="size-5" style={{ color: to }} aria-hidden="true" />
        </span>
        <span className="h-8 border-l-2 border-dashed border-neutral-300" />
        <span className="flex w-10 items-center justify-center font-gilroy text-[11px] font-bold text-neutral-900">A1</span>
      </span>
    )

  case 'donate':
    return (
      <span className="relative flex items-center justify-center">
        <Heart className={hover('size-12 fill-white text-white drop-shadow-lg group-hover:scale-110 group-focus-visible:scale-110')} aria-hidden="true" />
        <span className={hover('absolute -right-3 -top-1 flex size-5 items-center justify-center rounded-full bg-white text-[10px] font-bold text-neutral-900 shadow group-hover:-translate-y-1.5')} style={{ color: to }}>₮</span>
        <span className={hover('absolute -left-2 bottom-0 flex size-4 items-center justify-center rounded-full bg-white text-[8px] font-bold shadow group-hover:-translate-y-1')} style={{ color: from }}>₮</span>
      </span>
    )

  case 'email':
    return (
      <span className="flex w-32 flex-col gap-1.5">
        <Mail className={hover('size-6 self-center text-white group-hover:-translate-y-1 group-focus-visible:-translate-y-1')} aria-hidden="true" />
        <span className="flex h-6 items-center gap-1 rounded-full bg-white p-0.5 pl-2 shadow-lg">
          <span className="h-1.5 flex-1 rounded-full bg-neutral-200" />
          <span className="h-full w-7 rounded-full" style={gradient} />
        </span>
      </span>
    )

  case 'map':
    return (
      <span className="relative flex h-14 w-24 items-center justify-center overflow-hidden rounded-lg bg-white/10 ring-1 ring-white/15">
        {/* Streets, as a few crossing lines. */}
        <span className="absolute inset-x-0 top-1/3 h-px rotate-6 bg-white/30" />
        <span className="absolute inset-x-0 top-2/3 h-px -rotate-3 bg-white/20" />
        <span className="absolute inset-y-0 left-1/3 w-px -rotate-12 bg-white/25" />
        <MapPin className={hover('relative size-7 fill-white text-white drop-shadow-lg group-hover:-translate-y-1.5 group-focus-visible:-translate-y-1.5')} style={{ color: to }} aria-hidden="true" />
      </span>
    )

  case 'poll':
    return (
      <span className="flex w-28 flex-col gap-1.5">
        { [{ width: 'w-[78%]', hovered: 'group-hover:scale-x-[0.6]' }, { width: 'w-[52%]', hovered: 'group-hover:scale-x-[1.7]' }, { width: 'w-[28%]', hovered: 'group-hover:scale-x-110' }].map((bar, index) => (
          <span key={index} className="h-2.5 w-full overflow-hidden rounded-full bg-white/20">
            <span
              className={hover(`block h-full origin-left rounded-full ${bar.width} ${bar.hovered}`)}
              style={{ background: index === 0 ? '#fff' : 'rgb(255 255 255 / 0.55)' }}
            />
          </span>
        )) }
      </span>
    )

  case 'countdown':
    return (
      <span className="flex items-baseline gap-0.5 font-gilroy text-2xl font-bold tabular-nums leading-none text-white">
        <span>12</span>
        <span className="text-white/40">:</span>
        <span>08</span>
        <span className="text-white/40">:</span>
        <span className={hover('inline-block group-hover:-translate-y-0.5 group-hover:scale-110')}>45</span>
      </span>
    )

  case 'wishlist':
    return (
      <span className="flex items-end gap-1.5">
        <span className={hover('size-10 rounded-lg bg-white/90 shadow-md group-hover:-rotate-6 group-focus-visible:-rotate-6')} style={{ background: `linear-gradient(160deg, #fff, ${from}66)` }} />
        <span className={hover('flex size-12 items-center justify-center rounded-lg bg-white shadow-lg group-hover:-translate-y-1 group-focus-visible:-translate-y-1')}>
          <Gift className="size-6" style={{ color: to }} aria-hidden="true" />
        </span>
        <span className={hover('size-10 rounded-lg shadow-md group-hover:rotate-6 group-focus-visible:rotate-6')} style={{ background: `linear-gradient(200deg, #fff, ${to}66)` }} />
      </span>
    )

  case 'qa':
    return (
      <span className="flex w-28 flex-col gap-1.5">
        <span className={hover('h-6 w-20 rounded-2xl rounded-bl-md bg-white shadow group-hover:-translate-y-0.5')} />
        <span className={hover('h-6 w-16 self-end rounded-2xl rounded-br-md shadow group-hover:translate-y-0.5')} style={{ background: 'rgb(255 255 255 / 0.4)' }} />
      </span>
    )

  // The pet itself, hopping, rather than a drawing of a pet: the tile is the
  // first place an owner meets it.
  case 'pet':
    return (
      <span aria-hidden="true" className={hover('flex size-20 items-center justify-center group-hover:-translate-y-1 group-focus-visible:-translate-y-1')}>
        <PetSprite look={MINI_PET} mood="happy" stage="teen" label="" className="size-20 drop-shadow-lg" />
      </span>
    )

  case 'heading':
    return (
      <span className="flex w-28 flex-col gap-1.5">
        <span className="flex items-center gap-2">
          <span className={hover('h-3.5 w-16 origin-left rounded-full bg-white shadow group-hover:scale-x-110 group-focus-visible:scale-x-110')} />
          <span className="h-0.5 flex-1 rounded-full bg-white/45" />
        </span>
        <span className="h-1.5 w-12 rounded-full bg-white/55" />
      </span>
    )

  case 'divider':
    return (
      <span className="flex w-28 flex-col gap-2.5">
        <span className={hover('h-0.5 rounded-full bg-white group-hover:-translate-y-1 group-focus-visible:-translate-y-1')} />
        <span className="flex justify-between px-0.5">
          { Array.from({ length: 9 }, (_, index) => <span key={index} className="size-1 rounded-full bg-white/75" />) }
        </span>
        <svg viewBox="0 0 112 8" className={hover('h-2 w-full text-white/85 group-hover:translate-y-1 group-focus-visible:translate-y-1')} aria-hidden="true">
          <path d="M0 4 Q7 0 14 4 T28 4 T42 4 T56 4 T70 4 T84 4 T98 4 T112 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </span>
    )

  case 'qr':
    return (
      <span className={hover('relative grid size-16 grid-cols-5 grid-rows-5 gap-[2px] overflow-hidden rounded-xl bg-white p-1.5 shadow-lg group-hover:-rotate-3 group-focus-visible:-rotate-3')}>
        { QR_MINI.map((on, index) => <span key={index} className="rounded-[2px]" style={{ background: on ? '#111113' : 'transparent' }} />) }
        {/* The scan line, which only moves while the tile is hovered. */}
        <span className={hover('absolute inset-x-1 top-1.5 h-0.5 rounded-full opacity-0 group-hover:translate-y-11 group-hover:opacity-100 group-focus-visible:translate-y-11 group-focus-visible:opacity-100')} style={{ background: to }} />
      </span>
    )

  case 'hours':
    return (
      <span className={hover('flex w-32 flex-col gap-2 rounded-xl bg-white p-2.5 shadow-lg group-hover:-translate-y-1 group-focus-visible:-translate-y-1')}>
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-full bg-emerald-500" />
          <span className="font-gilroy text-[11px] font-bold tabular-nums text-neutral-900">09:00–18:00</span>
        </span>
        <span className="grid grid-cols-7 gap-1">
          { [1, 1, 1, 1, 1, 0, 0].map((open, index) => (
            <span
              key={index}
              className={index === 3 ? hover('h-2 origin-bottom rounded-[3px] group-hover:scale-y-150') : 'h-2 rounded-[3px]'}
              style={{ background: !open ? '#e5e5e5' : index === 3 ? to : `${from}99` }}
            />
          )) }
        </span>
      </span>
    )

  case 'bank':
    return (
      <span className={hover('flex w-32 flex-col gap-2 rounded-xl bg-white p-2.5 shadow-lg group-hover:-translate-y-1 group-focus-visible:-translate-y-1')}>
        <span className="flex items-center gap-1.5">
          <span className="flex size-5 items-center justify-center rounded-md text-[10px] font-bold text-white" style={gradient}>₮</span>
          <span className="h-1.5 w-12 rounded-full bg-neutral-200" />
        </span>
        <span className="flex items-center justify-between gap-1 rounded-lg px-1.5 py-1" style={{ background: `${from}1f` }}>
          <span className="font-gilroy text-[12px] font-bold tabular-nums text-neutral-900">5012 3456</span>
          <span className={hover('flex size-5 items-center justify-center rounded-full bg-white shadow-sm group-hover:scale-110 group-focus-visible:scale-110')} style={{ color: to }}>
            <Copy className="size-3" aria-hidden="true" />
          </span>
        </span>
      </span>
    )

  case 'chat':
    return (
      <span className="flex items-center gap-1.5">
        { CHAT_MINI.map(({ Icon, color }, index) => (
          <span
            key={index}
            className={hover(`flex items-center justify-center rounded-full border-2 border-white/80 shadow-lg ${
              index === 0 ? 'size-12 group-hover:-translate-y-1 group-focus-visible:-translate-y-1' : 'size-9'
            }`)}
            style={{ background: color, transitionDelay: `${index * 40}ms` }}
          >
            <Icon className={`${index === 0 ? 'size-5' : 'size-4'} text-white`} aria-hidden="true" />
          </span>
        )) }
      </span>
    )

  case 'promo':
    return (
      <span className={hover('relative flex h-12 w-28 items-center gap-2 rounded-lg bg-white px-3 shadow-lg group-hover:rotate-[4deg] group-focus-visible:rotate-[4deg]')}>
        {/* The coupon's notches. */}
        <span className="absolute -left-1.5 top-1/2 size-3 -translate-y-1/2 rounded-full bg-black/40" />
        <span className="absolute -right-1.5 top-1/2 size-3 -translate-y-1/2 rounded-full bg-black/40" />
        <span className="font-gilroy text-[17px] font-bold leading-none" style={{ color: to }}>%</span>
        <span className="flex h-7 flex-1 items-center justify-center rounded-md border-2 border-dashed font-mono text-[11px] font-bold tracking-wider text-neutral-900" style={{ borderColor: `${to}99` }}>-10</span>
      </span>
    )

  case 'blog':
    return (
      <span className="relative h-16 w-24">
        {/* A second sheet under the first: a blog is more than one post. */}
        <span className={hover('absolute inset-0 rotate-6 rounded-lg bg-white/40 shadow-md group-hover:rotate-10 group-focus-visible:rotate-10')} />
        <span className={hover('absolute inset-0 flex -rotate-4 flex-col overflow-hidden rounded-lg bg-white shadow-lg group-hover:-translate-y-1 group-hover:-rotate-7 group-focus-visible:-translate-y-1 group-focus-visible:-rotate-7')}>
          {/* The cover strip, then the title and two lines of body. */}
          <span className="h-5 w-full shrink-0" style={gradient} />
          <span className="flex flex-1 flex-col justify-center gap-1 px-2">
            <span className="h-1.5 w-4/5 rounded-full bg-neutral-800" />
            <span className="h-1 w-full rounded-full bg-neutral-300" />
            <span className="h-1 w-3/5 rounded-full bg-neutral-300" />
            <span className="mt-0.5 h-1 w-6 rounded-full" style={{ background: to }} />
          </span>
        </span>
      </span>
    )
  }
}
